import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Link,
    Paper,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { patchAccountFromResponse } from '../../components/subscription/subscriptionPlanMigration';
import { canonicalPlanName } from '../../helpers/subscription';

/**
 * MP often appends ?preapproval_id=… even when back_url already has a query,
 * producing …?status=success?preapproval_id=…. Parse both forms.
 */
function parseReturnParams(searchParams) {
    let status = (searchParams.get('status') || '').toLowerCase();
    let preapprovalID = searchParams.get('preapproval_id')
        || searchParams.get('preapprovalId')
        || searchParams.get('collection_id')
        || '';

    if (status.includes('preapproval_id=') || status.includes('?')) {
        const hrefMatch = window.location.href.match(/preapproval_id=([^&?#]+)/i);
        if (hrefMatch) {
            preapprovalID = decodeURIComponent(hrefMatch[1]);
        }
        status = status.split(/[?&]/)[0];
    }

    if (!preapprovalID) {
        const hrefMatch = window.location.href.match(/preapproval_id=([^&?#]+)/i);
        if (hrefMatch) {
            preapprovalID = decodeURIComponent(hrefMatch[1]);
        }
    }

    if (!status) {
        status = 'pending';
    }

    return { status, preapprovalID };
}

function SubscriptionReturnPage() {
    const [searchParams] = useSearchParams();
    const { status, preapprovalID } = parseReturnParams(searchParams);

    const [loading, setLoading] = useState(true);
    const [checkoutStatus, setCheckoutStatus] = useState(null);
    const [error, setError] = useState('');
    const attemptsRef = useRef(0);

    const loadStatus = useCallback(async () => {
        if (!preapprovalID) {
            setLoading(false);
            return false;
        }
        try {
            const response = await api.get('/me/subscription/checkout/status', {
                params: { preapproval_id: preapprovalID },
            });
            const data = response?.data?.data || null;
            setCheckoutStatus(data);
            if (data?.plan) {
                patchAccountFromResponse(data.plan);
            }
            setError('');
            return data?.mp_subscription_status === 'authorized';
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível confirmar o status da assinatura.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [preapprovalID]);

    useEffect(() => {
        let cancelled = false;
        let timer;

        const tick = async () => {
            const authorized = await loadStatus();
            if (cancelled || authorized || !preapprovalID) {
                return;
            }
            attemptsRef.current += 1;
            if (attemptsRef.current >= 8) {
                return;
            }
            timer = window.setTimeout(tick, 2500);
        };

        tick();

        return () => {
            cancelled = true;
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loadStatus, preapprovalID]);

    const planName = canonicalPlanName(checkoutStatus?.plan?.name || '—');
    const isSuccess = status === 'success'
        || checkoutStatus?.mp_subscription_status === 'authorized';
    const isFailure = status === 'failure';
    const isPending = !isSuccess && !isFailure;

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} to="/admin/my-account/subscription" underline="hover" color="inherit">
                    Minha assinatura
                </Link>
                <Typography color="text.primary">Confirmação</Typography>
            </Breadcrumbs>

            <Paper variant="outlined" sx={{ p: 4, maxWidth: 640 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={28} />
                        <Typography>Confirmando sua assinatura…</Typography>
                    </Box>
                ) : null}

                {!loading && error ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                {!loading && isSuccess ? (
                    <>
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Assinatura confirmada
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            {checkoutStatus?.trial_active
                                ? `Seu período de teste no plano ${planName} já começou.`
                                : `Seu plano ${planName} está ativo.`}
                        </Typography>
                    </>
                ) : null}

                {!loading && isFailure ? (
                    <>
                        <ErrorOutlineIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Pagamento não concluído
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Não foi possível confirmar a assinatura. Tente novamente em Minha assinatura.
                        </Typography>
                    </>
                ) : null}

                {!loading && isPending && !isSuccess && !isFailure ? (
                    <>
                        <HourglassEmptyIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h5" sx={{ mb: 1 }}>
                            Processando assinatura
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Recebemos seu retorno do Mercado Pago. A confirmação pode levar alguns instantes.
                        </Typography>
                    </>
                ) : null}

                <Button variant="contained" component={RouterLink} to="/admin/my-account/subscription">
                    Ir para Minha assinatura
                </Button>
            </Paper>
        </Box>
    );
}

export default SubscriptionReturnPage;
